/**
 * Devices view model (#999 Story 3.5).
 *
 * Pure aggregation for the Devices screen. Given the six repeatable-unit lists,
 * location tags, and enabled steps, produces location cards (creation order;
 * empty tags included; untagged last) with units grouped by equipment type.
 *
 * Summary metrics retained on the view model; title badge removed (product 2026-07-14).
 */

import {
  mapUnitToDeviceRow,
  type DeviceRow,
  type DeviceSourceUnit,
  type DeviceStepId,
  type DeviceTableLayout,
} from './deviceRowMapper';
import {
  sortLocationTagsByCreationOrder,
  type LocationTagGroupingInput,
} from './groupUnitsByLocationThenStatus';
import { isRepeatableUnitStep } from './sidebarStepProgress';
import { getRequestFormStepCatalogEntry } from '../config/requestFormStepRegistry';

export type { DeviceRow, DeviceStepId } from './deviceRowMapper';

import type { CoolingSystemConfiguration } from '../types/coolingSystem';
import type { LargeUpsConfiguration } from '../types/largeUps';
import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';
import type { PowerDistributionConfiguration } from '../types/powerDistribution';
import type { RackConfiguration } from '../types/rack';
import type { SmallUpsConfiguration } from '../types/smallUps';

/** Units for one equipment type inside a location card. */
export interface DeviceTypeGroup {
  stepId: DeviceStepId;
  stepTitle: string;
  units: DeviceSourceUnit[];
}

/** One location card (a tag, or the bottom Untagged card). */
export interface DeviceLocationCard {
  locationTagId: number | null;
  locationTagName: string | null;
  isUntagged: boolean;
  isEmpty: boolean;
  deviceCount: number;
  groups: DeviceTypeGroup[];
}

export interface DevicesSummaryMetrics {
  locations: number;
  totalDevices: number;
  existing: number;
  newCount: number;
  /** Title chip only — e.g. "6 devices · 2 locations". */
  badge: string;
}

export interface DevicesViewModel {
  cards: DeviceLocationCard[];
  summary: DevicesSummaryMetrics;
}

export interface DevicesViewModelInput {
  tags: LocationTagGroupingInput[];
  enabledStepIds: readonly string[];
  racks: readonly RackConfiguration[];
  smallUps: readonly SmallUpsConfiguration[];
  largeUps: readonly LargeUpsConfiguration[];
  powerDistribution: readonly PowerDistributionConfiguration[];
  coolingSystems: readonly CoolingSystemConfiguration[];
  liquidCoolingCdus: readonly LiquidCoolingCduConfiguration[];
  layout?: DeviceTableLayout;
}

interface TaggedUnitEntry {
  unit: DeviceSourceUnit;
  stepId: DeviceStepId;
  locationTagId: number | null;
}

const DEVICE_STEP_ORDER: readonly DeviceStepId[] = [
  'rack-power',
  'small-ups',
  'large-ups',
  'power-distribution',
  'cooling-readiness',
  'liquid-cooling',
];

function compareRows(a: DeviceRow, b: DeviceRow): number {
  if (a.status !== b.status) {
    return a.status === 'existing' ? -1 : 1;
  }
  if (a.displayOrder !== b.displayOrder) {
    return a.displayOrder - b.displayOrder;
  }
  return a.id - b.id;
}

function pluralize(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

function buildTypeGroups(
  entries: TaggedUnitEntry[],
  layout: DeviceTableLayout,
): DeviceTypeGroup[] {
  const byStep = new Map<
    DeviceStepId,
    Array<{ unit: DeviceSourceUnit; row: DeviceRow }>
  >();

  for (const entry of entries) {
    const row = mapUnitToDeviceRow(entry.stepId, entry.unit, 0, layout);
    if (!row) {
      continue;
    }
    const bucket = byStep.get(entry.stepId) ?? [];
    bucket.push({ unit: entry.unit, row });
    byStep.set(entry.stepId, bucket);
  }

  const groups: DeviceTypeGroup[] = [];
  for (const stepId of DEVICE_STEP_ORDER) {
    const bucket = byStep.get(stepId);
    if (!bucket || bucket.length === 0) {
      continue;
    }
    bucket.sort((a, b) => compareRows(a.row, b.row));
    groups.push({
      stepId,
      stepTitle: getRequestFormStepCatalogEntry(stepId)?.title ?? stepId,
      units: bucket.map(item => item.unit),
    });
  }

  return groups;
}

export function buildDevicesViewModel(
  input: DevicesViewModelInput,
): DevicesViewModel {
  const {
    tags,
    enabledStepIds,
    racks,
    smallUps,
    largeUps,
    powerDistribution,
    coolingSystems,
    liquidCoolingCdus,
    layout = 'desktop',
  } = input;

  const enabledRepeatable = new Set(
    enabledStepIds.filter(isRepeatableUnitStep),
  );

  const sources: Array<[DeviceStepId, readonly DeviceSourceUnit[]]> = [
    ['rack-power', racks],
    ['small-ups', smallUps],
    ['large-ups', largeUps],
    ['power-distribution', powerDistribution],
    ['cooling-readiness', coolingSystems],
    ['liquid-cooling', liquidCoolingCdus],
  ];

  const sortedTags = sortLocationTagsByCreationOrder(tags).filter(
    (tag): tag is { id: number; name: string } => typeof tag.id === 'number',
  );
  const knownTagIds = new Set(sortedTags.map(tag => tag.id));

  const allEntries: TaggedUnitEntry[] = [];
  for (const [stepId, list] of sources) {
    if (!enabledRepeatable.has(stepId)) {
      continue;
    }
    for (const unit of list) {
      const row = mapUnitToDeviceRow(stepId, unit, 0, layout);
      if (!row) {
        continue;
      }
      const rawTagId = unit.locationTagId ?? null;
      const locationTagId =
        rawTagId != null && knownTagIds.has(rawTagId) ? rawTagId : null;
      allEntries.push({ unit, stepId, locationTagId });
    }
  }

  const taggedById = new Map<number, TaggedUnitEntry[]>();
  const untaggedEntries: TaggedUnitEntry[] = [];
  for (const entry of allEntries) {
    if (entry.locationTagId == null) {
      untaggedEntries.push(entry);
      continue;
    }
    const bucket = taggedById.get(entry.locationTagId) ?? [];
    bucket.push(entry);
    taggedById.set(entry.locationTagId, bucket);
  }

  const cards: DeviceLocationCard[] = sortedTags.map(tag => {
    const entries = taggedById.get(tag.id) ?? [];
    return {
      locationTagId: tag.id,
      locationTagName: tag.name,
      isUntagged: false,
      isEmpty: entries.length === 0,
      deviceCount: entries.length,
      groups: buildTypeGroups(entries, layout),
    };
  });

  if (untaggedEntries.length > 0) {
    cards.push({
      locationTagId: null,
      locationTagName: null,
      isUntagged: true,
      isEmpty: false,
      deviceCount: untaggedEntries.length,
      groups: buildTypeGroups(untaggedEntries, layout),
    });
  }

  const totalDevices = allEntries.length;
  const existing = allEntries.filter(entry => {
    const row = mapUnitToDeviceRow(entry.stepId, entry.unit, 0, layout);
    return row?.status === 'existing';
  }).length;
  const newCount = totalDevices - existing;
  const locations = sortedTags.length;

  return {
    cards,
    summary: {
      locations,
      totalDevices,
      existing,
      newCount,
      badge: `${pluralize(totalDevices, 'device')} · ${pluralize(
        locations,
        'location',
      )}`,
    },
  };
}
